import { useEffect, useRef, useCallback } from "react";
import AuthProvider from "./authService";
import { HubConnectionState } from "@microsoft/signalr";
import { closeConnection } from "../utility/Dashboard/utility";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../store/combinedReducer";

const REFRESH_LOCK_KEY = "token_refresh_lock";
const LOG_PREFIX = "[TokenAutoRefresh]";
const LOCK_TIMEOUT = 15000;
const BUFFER_TIME = 90 * 1000;

export const useTokenAutoRefresh = () => {
  const dispatch = useDispatch();
  const timeoutIdRef = useRef<number | null>(null);
  const logIntervalIdRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);
  const tabId = useRef(`tab_${Date.now()}_${Math.random()}`);
  const signalRConnection = useSelector(
    (state: AppState) => state.reducer.signalR
  );

  const acquireLock = () => {
    const now = Date.now();
    const lockData = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );

    if (!lockData || now - lockData.timestamp > LOCK_TIMEOUT) {
      localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ timestamp: now, tabId: tabId.current })
      );
      return true;
    }

    return lockData.tabId === tabId.current;
  };

  const releaseLock = () => {
    const lockData = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );
    if (lockData?.tabId === tabId.current) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  };

  const scheduleRefresh = useCallback(() => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    const expiryTimeRaw = localStorage.getItem("token_expiry_time");
    const expiryTime = Number(expiryTimeRaw);
    const now = Date.now();

    if (!expiryTimeRaw || isNaN(expiryTime) || expiryTime <= now) {
      timeoutIdRef.current = window.setTimeout(scheduleRefresh, 5000);
      return;
    }

    const refreshDelay = Math.max(expiryTime - now - BUFFER_TIME, 1000);

    timeoutIdRef.current = window.setTimeout(async () => {
      if (isRefreshingRef.current) return;

      if (!acquireLock()) {
        // console.log(`${LOG_PREFIX} Another tab holds the lock. Waiting.`);
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 2000);
        return;
      }

      // Double-check after short delay
      await new Promise((res) => setTimeout(res, 100));
      const lockData = JSON.parse(
        localStorage.getItem(REFRESH_LOCK_KEY) || "null"
      );
      if (lockData?.tabId !== tabId.current) {
        // console.log(`${LOG_PREFIX} Lock stolen by another tab. Aborting.`);
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 2000);
        return;
      }

      const isLoggedIn =
        localStorage.getItem("logged_user") === JSON.stringify(true);
      if (isLoggedIn) {
        isRefreshingRef.current = true;
        try {
          // console.log(
          //   `${LOG_PREFIX} This tab (${tabId.current}) is refreshing token.`
          // );

          closeConnection(signalRConnection, HubConnectionState, dispatch);
          await AuthProvider().getRefreshToken();

          window.dispatchEvent(
            new CustomEvent("custom-storage", {
              detail: { key: "token_expiry_time", value: expiryTimeRaw },
            })
          );

          console.log(`${LOG_PREFIX} Token refresh successful.`);
        } catch (error) {
          console.error(`${LOG_PREFIX} Token refresh failed:`, error);
          timeoutIdRef.current = window.setTimeout(scheduleRefresh, 10000);
        } finally {
          releaseLock();
          isRefreshingRef.current = false;
        }
      }
    }, refreshDelay);
  }, [signalRConnection]);

  const startStatusLogger = useCallback(() => {
    if (logIntervalIdRef.current) clearInterval(logIntervalIdRef.current);

    logIntervalIdRef.current = window.setInterval(() => {
      const expiryTimeRaw = localStorage.getItem("token_expiry_time");
      if (!expiryTimeRaw) return;

      const expiryTime = Number(expiryTimeRaw);
      const now = Date.now();
      const timeUntilRefresh = Math.max(expiryTime - now - BUFFER_TIME, 0);

      if (timeUntilRefresh > 0) {
        const minutes = Math.floor(timeUntilRefresh / 60000);
        const seconds = Math.floor((timeUntilRefresh % 60000) / 1000);
        // console.log(
        //   `${LOG_PREFIX} Time until next refresh: ${minutes}m ${seconds}s`
        // );
      } else {
        // console.log(`${LOG_PREFIX} Token expired or refresh is due.`);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "token_expiry_time" || event.key === REFRESH_LOCK_KEY) {
        scheduleRefresh();
        startStatusLogger();
      }
    };

    const handleCustomStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === "token_expiry_time") {
        scheduleRefresh();
        startStatusLogger();
      }
    };

    const handleBeforeUnload = () => {
      releaseLock();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("custom-storage", handleCustomStorageChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    scheduleRefresh();
    startStatusLogger();

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (logIntervalIdRef.current) clearInterval(logIntervalIdRef.current);

      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("custom-storage", handleCustomStorageChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      handleBeforeUnload();
    };
  }, [scheduleRefresh, startStatusLogger]);
};
